/**
 * @file EditExtractedInformation.tsx
 *
 * @description This component is a comprehensive form interface for police officers
 * to review and edit extracted data from video evidence. It provides a multi-section
 * form with validation, field formatting, and confirmation workflows to ensure accurate data collection.
 *
 * @module Components
 * @requires Material-UI
 * @requires React
 * @requires axios
 */

import {
  Box,
  Typography,
  Divider,
  CircularProgress,
  Fade,
  Paper,
  Chip,
  Switch,
  FormControlLabel,
  Tooltip,
  Alert,
  Skeleton,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import { useState, useRef, useEffect, useMemo } from "react";
import { VideoPreviewV2 } from "../../components/video/VideoPreviewV2";
import { SnackBarMessage } from "../../modals/SnackBarMessage";
import { useErrorSnackbar } from "../../shared/hooks/useErrorSnackbar";
import { useSnackbarPosition } from "../../shared/hooks/useSnackbarPosition";
import Form from "@rjsf/mui";
import { customizeValidator } from "@rjsf/validator-ajv8";
import { customWidgets } from "../../components/widgets/widgetRegistry";
import { generateUiSchema } from "../../utils/generateUiSchema";
import { transformErrors } from "../../utils/transformErrors";
import { getObjectSchemaAndData } from "../../utils/getObjectSchemaAndData";
import { LayoutGridField } from "../../components/fields/LayoutGridField";
import {
  AddButton,
  RemoveButton,
  DescriptionFieldTemplate,
  ArrayFieldItemTemplate,
  ObjectFieldTemplate,
  ErrorListTemplate,
  CustomArrayFieldTemplate,
} from "../../components/jsrfTemplates";
import { useFormDataStore } from "../../store/formDataStore";

import { ExtractedInformationProps } from "./types";
import {
  initializeNullArrays,
  getReportInformationProperties,
} from "./formUtils";
import { useSectionNavigation, useFormSections } from "./hooks";
import {
  SectionProgressBar,
  FormNavigation,
  FormSubmitArea,
  ConfirmationCheckbox,
} from "./components";
import { formatSchemaName } from "../../utils/formatSchemaName";
import { RJSFSchema, UiSchema } from "@rjsf/utils";
import { IChangeEvent } from "@rjsf/core";
import { NarrativeOnlyWarningModal } from "../../modals/NarrativeOnlyWarningModal";
import { NarrativeOnlyBanner } from "../../components/feedback/NarrativeOnlyBanner";
import { useAuthContext } from "../../context/AuthContext";
import { useNarrativeOnlyToggleEnabled } from "../../hooks/useNarrativeOnlyToggleEnabled";
const LOCAL_STORAGE_EDIT_BANNER_KEY = "edit:narrativeOnlyBannerDismissed";

const validator = customizeValidator({
  customFormats: {
    int: (_data: any) => true,
    double: (_data: any) => true,
  },
});

/**
 * @name EditExtractedInformation
 *
 * @description Component for editing information extracted from law enforcement video evidence
 *
 * This component provides a multi-section form interface for officers to review and modify
 * automatically extracted information from video evidence. It includes features like:
 * - Section navigation with progress tracking
 * - Array item management (add/delete/navigate)
 * - Field validation based on model schema
 * - Confirmations for each section and final submission
 * - Undo functionality for changes (section-specific or all changes)
 * - Special handling for narrative text with "easter egg" validation
 *
 * @returns {JSX.Element} A comprehensive form interface to edit extracted report information.
 */
export const EditExtractedInformation: React.FC<ExtractedInformationProps> = ({
  formData,
  jsonSchema,
  schemaMapping,
  onUpdate,
  onSubmit,
  videoData,
  videoLoadStatus,
  handleUserActivity,
  liveWebSocket,
  allowEditing = true,
  handleSaveChanges,
  showError: propShowError,
  reportId,
  reportType,
  agentApiEndpoint,
  isSaving = false,
  fetchReportData,
  formDataSnapshotRef,
  userProfile,
  chatSendMessageRef,
  setShowChat,
  showRMSCommit,
}) => {
  const theme = useTheme();
  const { organizationId } = useAuthContext();

  // Navigation state - tracks current section and array indices
  const currentSection = useFormDataStore((state) => state.currentSection);
  const setCurrentSection = useFormDataStore(
    (state) => state.setCurrentSection,
  );

  const [, setHasFormErrors] = useState(false);
  // isNavigating is handled in hook

  // State for page confirmations
  const [pageConfirmations, setPageConfirmations] = useState<{
    [key: number]: boolean;
  }>({});

  const [finalConfirmation, setFinalConfirmation] = useState(false);

  // Track sections modified by AI that need officer attention
  const [sectionsNeedingAttention, setSectionsNeedingAttention] = useState<{
    [key: number]: boolean;
  }>({});

  // Video preview control state
  const [isVideoMinimized, setIsVideoMinimized] = useState(true);

  // Narrative-only warning modal state
  const [showNarrativeOnlyWarning, setShowNarrativeOnlyWarning] =
    useState(false);

  const getInitialEditBannerState = () => {
    try {
      const storedValue = localStorage.getItem(LOCAL_STORAGE_EDIT_BANNER_KEY);
      return storedValue !== "true";
    } catch {
      return true;
    }
  };

  // Onboarding banner for narrative-only toggle (shown once per browser)
  const [showEditBanner, setShowEditBanner] = useState(
    getInitialEditBannerState,
  );

  const handleDismissEditBanner = () => {
    setShowEditBanner(false);
    try {
      localStorage.setItem(LOCAL_STORAGE_EDIT_BANNER_KEY, "true");
    } catch {
      // Ignore storage failures; banner will reappear on refresh.
    }
  };

  // References for DOM elements - used for scrolling behavior
  const itemRefs = useRef<HTMLDivElement[]>([]);
  const sectionBarRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);

  // Clear item refs when changing sections
  useEffect(() => {
    itemRefs.current = [];
  }, [currentSection]);

  const handleToggleVideoMinimize = () => {
    setIsVideoMinimized((prev) => !prev);
  };

  // Hook for snackbar notifications (errors, warnings, info)
  const { snackBarMessage, snackBarMode, handleCloseSnackbar, showWarning } =
    useErrorSnackbar();
  const snackbarPosition = useSnackbarPosition();

  /**
   * @name scrollToTop
   *
   * @description Scrolls to the section progress bar so navigation lands on
   * the nav pills. Skips the scroll entirely when the bar is already at or
   * below the top of the viewport (user is above the anchor already).
   */
  const scrollToTop = () => {
    setTimeout(() => {
      formRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      const target = sectionBarRef.current;
      if (!target) return;
      if (target.getBoundingClientRect().top >= 0) return;
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const {
    sections,
    totalSections,
    generatedUiSchema: propGeneratedUiSchema,
  } = useFormSections(jsonSchema, schemaMapping);

  const narrativeOnlyToggleEnabled = useNarrativeOnlyToggleEnabled();

  // Narrative-only mode: defaults ON for non-RMS users, OFF for RMS users.
  // Forced OFF when the feature flag is disabled.
  // Initialized to null so the default can be applied after async inputs
  // (feature flags, showRMSCommit) settle, without overwriting a user toggle.
  const [narrativeOnly, setNarrativeOnly] = useState<boolean | null>(null);

  useEffect(() => {
    if (narrativeOnly === null) {
      setNarrativeOnly(narrativeOnlyToggleEnabled && !showRMSCommit);
    }
  }, [narrativeOnlyToggleEnabled, showRMSCommit, narrativeOnly]);

  // Find the actual index of the Narratives section in the full section list
  // Section 0 = General Info, so Narratives actual index = 1 + indexOf("Narratives")
  const narrativesIndexInSections = sections.indexOf("Narratives");
  const narrativesActualIndex =
    narrativesIndexInSections >= 0 ? narrativesIndexInSections + 1 : -1;

  // Build the visible section mapping.
  // In narrative-only mode the page renders General Info + Narratives stacked
  // inline as a single virtual section, so the progress bar collapses to one
  // entry (and hides, since visibleTotalSections === 1).
  const visibleSectionMap = useMemo(() => {
    if (!narrativeOnly || narrativesActualIndex < 0) {
      return Array.from({ length: totalSections }, (_, i) => ({
        visibleIndex: i,
        actualIndex: i,
        name: i === 0 ? "General Info" : sections[i - 1],
      }));
    }
    return [
      {
        visibleIndex: 0,
        actualIndex: 0,
        name: "Report",
      },
    ];
  }, [narrativeOnly, narrativesActualIndex, totalSections, sections]);

  const visibleTotalSections = visibleSectionMap.length;

  const visibleCurrentSection = useMemo(() => {
    const found = visibleSectionMap.find(
      (m) => m.actualIndex === currentSection,
    );
    return found ? found.visibleIndex : 0;
  }, [visibleSectionMap, currentSection]);

  const visibleSectionNames = useMemo(
    () => visibleSectionMap.map((m) => m.name),
    [visibleSectionMap],
  );

  const {
    isNavigating,
    handleSectionChangeForward,
    handleSectionChangeBackward,
    handleSidebarItemClick,
  } = useSectionNavigation(
    currentSection,
    totalSections,
    sections,
    setCurrentSection,
    setHasFormErrors,
    scrollToTop,
    handleUserActivity,
  );

  // Wrapped navigation for narrative-only mode
  const wrappedHandleSectionChangeForward = () => {
    if (narrativeOnly) return;
    handleSectionChangeForward();
  };

  const wrappedHandleSectionChangeBackward = () => {
    if (narrativeOnly) return;
    handleSectionChangeBackward();
  };

  const wrappedHandleSidebarItemClick = (visibleIndex: number) => {
    const mapping = visibleSectionMap[visibleIndex];
    if (mapping) handleSidebarItemClick(mapping.actualIndex);
  };

  // Toggle handler
  const handleNarrativeOnlyToggle = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const isOn = event.target.checked;
    setNarrativeOnly(isOn);
    if (isOn) {
      // Stacked General Info + Narratives view renders from currentSection=0;
      // pin to it so snapshot/agent-change-detection still resolves correctly.
      setCurrentSection(0);
      scrollToTop();
    }
    // Haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate(isOn ? [15, 30, 15] : 10);
    }
    handleUserActivity(`Toggle Narrative Only mode: ${isOn ? "ON" : "OFF"}`);
  };

  // Pin currentSection to 0 (General Info) when narrative-only is ON, since
  // the stacked view renders both General Info and Narratives from that index.
  useEffect(() => {
    if (narrativeOnly && currentSection !== 0) {
      setCurrentSection(0);
    }
  }, [narrativeOnly]);

  const handleChanges = (e: any) => {
    let updatedFormData = { ...formData };

    if (currentSection === 0) {
      updatedFormData = { ...updatedFormData, ...e.formData };
    } else {
      const sectionKey = sections[currentSection - 1];
      updatedFormData[sectionKey] = e.formData;
    }

    console.log("Form changes:", updatedFormData);
    onUpdate(updatedFormData, false);
    handleUserActivity("Edit form data event: " + JSON.stringify(e.formData));
  };

  // Dedicated change handlers for the narrative-only stacked view, where two
  // <Form> instances live on the same page and currentSection no longer maps
  // to a single form.
  const handleGeneralInfoChange = (e: IChangeEvent) => {
    const updatedFormData = { ...formData, ...e.formData };
    onUpdate(updatedFormData, false);
    handleUserActivity("Edit form data event: " + JSON.stringify(e.formData));
  };

  const handleNarrativesChange = (e: IChangeEvent) => {
    const updatedFormData = { ...formData, Narratives: e.formData };
    onUpdate(updatedFormData, false);
    handleUserActivity("Edit form data event: " + JSON.stringify(e.formData));
  };

  // Check confirmation status
  const isCurrentPageConfirmed = pageConfirmations[currentSection] === true;
  console.log(
    "Page confirmations:",
    pageConfirmations,
    "Current page confirmed:",
    isCurrentPageConfirmed,
  );
  const areAllPagesConfirmed =
    (narrativeOnly && isCurrentPageConfirmed) ||
    (() => {
      for (let i = 0; i < totalSections; i++) {
        if (!pageConfirmations[i]) return false;
      }
      return true;
    })(); // invoke immediately

  // Remap confirmations and attention states to visible indices for child components
  const visiblePageConfirmations = useMemo(() => {
    const mapped: { [key: number]: boolean } = {};
    visibleSectionMap.forEach((m) => {
      if (pageConfirmations[m.actualIndex]) mapped[m.visibleIndex] = true;
    });
    return mapped;
  }, [visibleSectionMap, pageConfirmations]);

  const visibleSectionsNeedingAttention = useMemo(() => {
    const mapped: { [key: number]: boolean } = {};
    visibleSectionMap.forEach((m) => {
      if (sectionsNeedingAttention[m.actualIndex])
        mapped[m.visibleIndex] = true;
    });
    return mapped;
  }, [visibleSectionMap, sectionsNeedingAttention]);

  const handleCheckBoxChange = (checked: boolean) => {
    setPageConfirmations({
      ...pageConfirmations,
      [currentSection]: checked,
    });

    // Clear attention state when officer confirms the section
    if (checked && sectionsNeedingAttention[currentSection]) {
      setSectionsNeedingAttention((prev) => {
        const updated = { ...prev };
        delete updated[currentSection];
        return updated;
      });
    }

    handleUserActivity(
      `Confirming page ${currentSection} with value ${checked}`,
    );
  };

  // Detect which sections changed after agent modification
  // The snapshot is captured in EditSectionPage before fetchReportData is called
  useEffect(() => {
    const beforeData = formDataSnapshotRef?.current;

    console.log("[Agent Change Detection] useEffect triggered:", {
      hasBeforeData: !!beforeData,
      hasFormData: !!formData,
      hasJsonSchema: !!jsonSchema,
      beforeOtherInvolved: beforeData?.OtherInvolved?.length,
      afterOtherInvolved: formData?.OtherInvolved?.length,
    });

    // Only process if we have a snapshot to compare against
    if (!beforeData || !formData || !jsonSchema) {
      return;
    }

    const changedIndices: number[] = [];

    // Check General Info (section 0) - non-array/object properties
    const oldGeneral = getReportInformationProperties(
      jsonSchema,
      beforeData,
    ).data;
    const newGeneral = getReportInformationProperties(
      jsonSchema,
      formData,
    ).data;
    if (JSON.stringify(oldGeneral) !== JSON.stringify(newGeneral)) {
      changedIndices.push(0);
      console.log("[Agent Change Detection] General Info changed");
    }

    // Check other sections (1+)
    sections.forEach((sectionKey, index) => {
      const oldSection = JSON.stringify(beforeData[sectionKey]);
      const newSection = JSON.stringify(formData[sectionKey]);
      if (oldSection !== newSection) {
        changedIndices.push(index + 1);
        console.log(
          `[Agent Change Detection] Section "${sectionKey}" (index ${index + 1}) changed`,
        );
      }
    });

    console.log(
      "[Agent Change Detection] Total changed sections:",
      changedIndices,
    );

    // Only reset confirmations and mark attention for pages that actually changed
    if (changedIndices.length > 0) {
      // Reset final confirmation - need to re-confirm after reviewing changes
      setFinalConfirmation(false);

      // Mark changed sections as needing attention
      setSectionsNeedingAttention((prev) => {
        const updated = { ...prev };
        changedIndices.forEach((i) => {
          updated[i] = true;
        });
        return updated;
      });

      // Reset confirmations ONLY for changed pages (keep unchanged pages confirmed)
      setPageConfirmations((prev) => {
        console.log(
          "[Agent Change Detection] Resetting confirmations for indices:",
          changedIndices,
          "from:",
          prev,
        );
        const updated = { ...prev };
        changedIndices.forEach((i) => {
          delete updated[i];
        });
        console.log("[Agent Change Detection] New confirmations:", updated);
        return updated;
      });
    }

    // Clear the snapshot after processing
    if (formDataSnapshotRef) {
      formDataSnapshotRef.current = null;
    }
  }, [formData, jsonSchema, sections, formDataSnapshotRef]);

  /**
   * @name handleSubmit
   *
   * @description Handles form submission after validation
   * Triggers onSubmit callback which handles navigation in parent component
   */
  const executeSubmit = async () => {
    try {
      // In narrative-only mode, skip the pre-save (which would persist the
      // full form). onSubmit() will perform the narrative-only filtered save.
      await onUpdate(formData, !narrativeOnly);
      // navigation handled in parent
      await onSubmit(narrativeOnly ?? false);
    } catch (error) {
      console.error("Error submitting data:", error);
    }
  };

  const handleSubmit = async () => {
    handleUserActivity("Submit Report");
    if (!areAllPagesConfirmed || !finalConfirmation) {
      showWarning(
        "Please confirm all pages and the final confirmation before submitting.",
      );
      return;
    }
    if (narrativeOnly) {
      setShowNarrativeOnlyWarning(true);
      return;
    }
    await executeSubmit();
  };

  const handleNarrativeOnlyWarningConfirm = async () => {
    setShowNarrativeOnlyWarning(false);
    await executeSubmit();
  };

  /**
   * @name scrollToItem
   *
   * @description Scrolls to a specific item in the array and highlights it briefly
   *
   * @param {number} index - Index of the item to scroll to
   */
  const scrollToItem = (index: number) => {
    const element = itemRefs.current[index];
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      // Add a brief highlight effect
      const originalBorder = element.style.border;
      const originalBoxShadow = element.style.boxShadow;
      element.style.border = "2px solid #1976d2";
      element.style.boxShadow = "0 0 10px rgba(25, 118, 210, 0.5)";

      setTimeout(() => {
        element.style.border = originalBorder;
        element.style.boxShadow = originalBoxShadow;
      }, 1500);
    }
  };

  const formContainerSx = {
    width: "100%",
    "& .MuiFormControl-root": { mb: 2 },
    "& .MuiFormControl-root:has(.MuiRadioGroup-root)": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    "& .MuiFormGroup-root.MuiRadioGroup-root": {
      justifyContent: "center",
    },
    "& .MuiGrid-root": { maxWidth: "100%" },
    "& .MuiOutlinedInput-root": {
      backgroundColor: "background.paper",
    },
    "& .field-object": { mb: 2 },
    "& .field-array": { mb: 3 },
    "& .array-item": {
      p: 2,
      mb: 2,
      border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
      borderRadius: 1,
      backgroundColor: "background.paper",
    },
    "& .MuiFormControlLabel-root": { ml: 0 },
    "& .MuiGrid-container": { alignItems: "flex-start" },
  } as const;

  const renderFormSection = ({
    header,
    badgeNumber,
    schema,
    data,
    uiSchema,
    onChange,
    sectionTitle,
  }: {
    header: string;
    badgeNumber?: number;
    schema: RJSFSchema;
    data: unknown;
    uiSchema: UiSchema;
    onChange: (e: IChangeEvent) => void;
    sectionTitle: string | undefined;
  }) => (
    <>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        {badgeNumber !== undefined && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor: theme.palette.primary.main,
              color: "white",
              fontWeight: 700,
              fontSize: "1rem",
              flexShrink: 0,
              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
            }}
          >
            {badgeNumber}
          </Box>
        )}
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {formatSchemaName(header)}
        </Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={0}>
        <Box sx={formContainerSx}>
          <Form
            schema={schema}
            liveValidate={true}
            formData={data}
            validator={validator}
            uiSchema={uiSchema}
            transformErrors={transformErrors}
            onChange={(e) => {
              onChange(e);
              setHasFormErrors((e.errors?.length ?? 0) > 0);
            }}
            autoComplete="on"
            templates={{
              ButtonTemplates: { AddButton, RemoveButton },
              DescriptionFieldTemplate,
              ArrayFieldItemTemplate,
              ObjectFieldTemplate,
              ErrorListTemplate,
              ArrayFieldTemplate: CustomArrayFieldTemplate,
            }}
            formContext={{
              itemRefs,
              sectionTitle,
              liveWebSocket,
              handleUserActivity,
              handleSaveChanges,
              showError: propShowError,
              reportId,
              reportType,
              agentApiEndpoint,
              fetchReportData,
              userProfile,
              chatSendMessage: chatSendMessageRef?.current,
              setShowChat,
              showAIControls: true,
              stationId: organizationId,
            }}
            widgets={customWidgets}
            fields={{ LayoutGridField }}
            readonly={!allowEditing || isSaving}
            disabled={!allowEditing || isSaving}
          />
        </Box>
      </Grid>
    </>
  );

  /**
   * @name renderMainContent
   *
   * @description Renders the main content area based on current section.
   * When narrative-only is ON, renders General Information + Narratives stacked
   * inline as a single page (no numbered badges, no section navigation).
   */
  const renderMainContent = () => {
    if (narrativeOnly) {
      const { data: generalInfoData, schema: generalInfoSchema } =
        getReportInformationProperties(jsonSchema, formData);
      const generalInfoUiSchema = generateUiSchema(
        generalInfoSchema as RJSFSchema,
        schemaMapping,
        jsonSchema as RJSFSchema,
      );

      const { schema: narrativesSchema, data: narrativesData } =
        getObjectSchemaAndData(
          jsonSchema,
          "Narratives",
          formData,
          initializeNullArrays,
        );
      const narrativesSectionUiSchema = propGeneratedUiSchema["Narratives"];
      const narrativesUiSchema = narrativesSectionUiSchema
        ? {
            "ui:submitButtonOptions":
              propGeneratedUiSchema["ui:submitButtonOptions"],
            "ui:options": propGeneratedUiSchema["ui:options"],
            ...narrativesSectionUiSchema,
          }
        : propGeneratedUiSchema;

      return (
        <>
          {renderFormSection({
            header: "General Information",
            schema: generalInfoSchema,
            data: generalInfoData,
            uiSchema: generalInfoUiSchema,
            onChange: handleGeneralInfoChange,
            sectionTitle: undefined,
          })}
          <Divider sx={{ my: 4 }} />
          {renderFormSection({
            header: "Narratives",
            schema: narrativesSchema,
            data: narrativesData,
            uiSchema: narrativesUiSchema,
            onChange: handleNarrativesChange,
            sectionTitle: "Narratives",
          })}
          <Box sx={{ mt: 1, textAlign: "center" }}>
            {allowEditing && (
              <ConfirmationCheckbox
                isConfirmed={isCurrentPageConfirmed}
                isDisabled={isSaving}
                onChange={handleCheckBoxChange}
              />
            )}
          </Box>
        </>
      );
    }

    let data = null;
    let schema = null;
    let header = null;
    let uiSchema = propGeneratedUiSchema;
    if (currentSection === 0) {
      ({ data, schema } = getReportInformationProperties(jsonSchema, formData));
      header = "General Information";
      uiSchema = generateUiSchema(
        schema as RJSFSchema,
        schemaMapping,
        jsonSchema as RJSFSchema,
      );
    } else {
      header = sections[currentSection - 1];
      ({ schema, data } = getObjectSchemaAndData(
        jsonSchema,
        header,
        formData,
        initializeNullArrays,
      ));
      const sectionUiSchema = propGeneratedUiSchema[header];
      if (sectionUiSchema) {
        uiSchema = {
          "ui:submitButtonOptions":
            propGeneratedUiSchema["ui:submitButtonOptions"],
          "ui:options": propGeneratedUiSchema["ui:options"],
          ...sectionUiSchema,
        };
      }
    }

    return (
      <>
        {renderFormSection({
          header: header!,
          badgeNumber: visibleCurrentSection + 1,
          schema,
          data,
          uiSchema,
          onChange: handleChanges,
          sectionTitle: sections[currentSection - 1],
        })}
        <Box sx={{ mt: 1, textAlign: "center" }}>
          {allowEditing && (
            <ConfirmationCheckbox
              isConfirmed={isCurrentPageConfirmed}
              isDisabled={isSaving}
              onChange={handleCheckBoxChange}
            />
          )}
        </Box>
      </>
    );
  };

  return (
    <Box sx={{ overscrollBehavior: "contain" }}>
      <SnackBarMessage
        message={snackBarMessage}
        mode={snackBarMode}
        handleClose={handleCloseSnackbar}
        horizontalAnchor="center"
        verticalAnchor="top"
        sxOverrides={snackbarPosition}
      />

      {narrativeOnlyToggleEnabled && (
        <NarrativeOnlyWarningModal
          open={showNarrativeOnlyWarning}
          onClose={() => setShowNarrativeOnlyWarning(false)}
          onConfirm={handleNarrativeOnlyWarningConfirm}
        />
      )}

      {/* Video Preview - Always at top */}
      {videoData ? (
        <Box sx={{ mb: 2 }}>
          <VideoPreviewV2
            videoData={videoData}
            isMinimized={isVideoMinimized}
            onToggleMinimize={handleToggleVideoMinimize}
          />
        </Box>
      ) : videoLoadStatus === "loading" ? (
        <Box sx={{ mb: 2 }}>
          <Skeleton
            variant="rectangular"
            height={72}
            sx={{ borderRadius: 2 }}
          />
        </Box>
      ) : (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Video evidence could not be loaded.
        </Alert>
      )}

      {/* Onboarding banner for narrative-only toggle */}
      {narrativeOnlyToggleEnabled && (
        <NarrativeOnlyBanner
          visible={showEditBanner}
          onDismiss={handleDismissEditBanner}
        />
      )}

      <Grid container spacing={2}>
        {/* Main Form */}
        <Grid size={{ xs: 12, md: 12 }}>
          <Paper
            sx={{
              p: 3,
              position: "relative",
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
              boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.06)}`,
            }}
          >
            {/* Narrative Only Toggle */}
            {narrativeOnlyToggleEnabled && narrativesActualIndex >= 0 && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                <Tooltip
                  title={
                    narrativeOnly
                      ? "Showing General Information and Narrative. Toggle off to view all sections."
                      : "Toggle on to show only General Information and Narrative."
                  }
                  arrow
                  placement="left"
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={narrativeOnly ?? false}
                        onChange={handleNarrativeOnlyToggle}
                        size="small"
                        color="success"
                        sx={{
                          "& .MuiSwitch-thumb": {
                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                          },
                          "& .MuiSwitch-track": {
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          },
                          "&:hover .MuiSwitch-thumb": {
                            boxShadow: `0 0 0 6px ${alpha(theme.palette.success.main, 0.12)}`,
                          },
                          "&:active .MuiSwitch-thumb": {
                            transform: "scale(0.9)",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{
                          color: "text.secondary",
                          transition: "color 0.2s ease-in-out",
                          userSelect: "none",
                        }}
                      >
                        Narrative Only
                      </Typography>
                    }
                    labelPlacement="start"
                    sx={{
                      mr: 0,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? alpha(theme.palette.common.white, 0.03)
                          : alpha(theme.palette.grey[100], 0.5),
                      boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}`,
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? alpha(theme.palette.common.white, 0.08)
                            : alpha(theme.palette.grey[200], 0.5),
                        borderColor: alpha(theme.palette.divider, 0.8),
                        boxShadow: `0 2px 6px ${alpha(theme.palette.common.black, 0.08)}`,
                      },
                      "&:active": {
                        transform: "scale(0.98)",
                      },
                    }}
                  />
                </Tooltip>
              </Box>
            )}

            {visibleTotalSections > 1 && (
              <Box ref={sectionBarRef} sx={{ scrollMarginTop: 8 }}>
                <SectionProgressBar
                  currentSection={visibleCurrentSection}
                  totalSections={visibleTotalSections}
                  sectionNames={visibleSectionNames}
                  pageConfirmations={visiblePageConfirmations}
                  sectionsNeedingAttention={visibleSectionsNeedingAttention}
                  allowEditing={allowEditing}
                  onSectionClick={wrappedHandleSidebarItemClick}
                  showWarning={showWarning}
                />
              </Box>
            )}

            {/* Item selector buttons for array sections */}
            {currentSection > 0 &&
              formData[sections[currentSection - 1]]?.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    mb: 2,
                    flexWrap: "wrap",
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    pb: 2,
                  }}
                >
                  {formData[sections[currentSection - 1]].map(
                    (_: unknown, index: number) => (
                      <Chip
                        key={index}
                        label={`${formatSchemaName(sections[currentSection - 1].replace(/s$/, ""))} ${index + 1}`.toUpperCase()}
                        onClick={() => scrollToItem(index)}
                        sx={{
                          fontWeight: 600,
                          borderRadius: "20px",
                          "&:hover": {
                            bgcolor: "primary.main",
                            color: "white",
                          },
                        }}
                      />
                    ),
                  )}
                </Box>
              )}

            <Box
              ref={formRef}
              sx={{
                pr: 2,
                boxSizing: "border-box",
                width: "100%",
                position: "relative",
              }}
            >
              <Fade in={isNavigating} timeout={200}>
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                    zIndex: 10,
                    borderRadius: 2,
                  }}
                >
                  <CircularProgress size={40} />
                </Box>
              </Fade>

              <Fade in={!isNavigating} timeout={300}>
                <Box>{renderMainContent()}</Box>
              </Fade>
            </Box>

            <FormNavigation
              currentSection={visibleCurrentSection}
              totalSections={visibleTotalSections}
              isNavigating={isNavigating}
              isSaving={isSaving}
              onPrevious={wrappedHandleSectionChangeBackward}
              onNext={wrappedHandleSectionChangeForward}
            />

            <FormSubmitArea
              currentSection={visibleCurrentSection}
              totalSections={visibleTotalSections}
              allowEditing={allowEditing}
              areAllPagesConfirmed={areAllPagesConfirmed}
              finalConfirmation={finalConfirmation}
              isSaving={isSaving}
              setFinalConfirmation={setFinalConfirmation}
              onSubmit={handleSubmit}
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
